import {
  color,
  interpolateBlues,
  interpolateBrBG,
  interpolateGreens,
  interpolateInferno,
  interpolateOrRd,
  interpolatePiYG,
  interpolatePRGn,
  interpolatePuOr,
  interpolatePuRd,
  interpolatePurples,
  interpolateRdBu,
  interpolateRdGy,
  interpolateRdYlBu,
  interpolateRdYlGn,
  interpolateReds,
  interpolateSpectral,
  interpolateViridis,
  interpolateYlGnBu,
  interpolateYlOrRd,
  scaleOrdinal,
  scaleSequential,
  schemeCategory10,
  schemePaired,
  schemePastel1,
  schemeSet1,
  schemeSet3,
  schemeTableau10,
} from "d3";
import { defineMessages, IntlShape } from "react-intl";

import { HexColor } from "@mapmycustomers/shared/types/AnyColor";

export enum ThemeCategory {
  SEQUENTIAL,
  DIVERGING,
  QUALITATIVE,
  CUSTOM,
}

const categoryMessages = defineMessages<ThemeCategory>({
  [ThemeCategory.CUSTOM]: {
    id: "pinLegends.themeCategory.custom",
    defaultMessage: "Custom",
  },
  [ThemeCategory.DIVERGING]: {
    id: "pinLegends.themeCategory.diverging",
    defaultMessage: "Diverging",
  },
  [ThemeCategory.QUALITATIVE]: {
    id: "pinLegends.themeCategory.qualitative",
    defaultMessage: "Qualitative",
  },
  [ThemeCategory.SEQUENTIAL]: {
    id: "pinLegends.themeCategory.sequential",
    defaultMessage: "Sequential",
  },
});

export const getThemeCategoryDisplayName = (intl: IntlShape, category: ThemeCategory) =>
  intl.formatMessage(categoryMessages[category]);

export interface ThemeDescriptor {
  category: ThemeCategory;
  getColorFn: (numberOfBuckets: number) => (index: number) => HexColor;
  name: string;
}

export const themes = [
  {
    category: ThemeCategory.SEQUENTIAL,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateBlues);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "blues",
  },
  {
    category: ThemeCategory.SEQUENTIAL,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateGreens);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "greens",
  },
  {
    category: ThemeCategory.SEQUENTIAL,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolatePurples);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "purples",
  },
  {
    category: ThemeCategory.SEQUENTIAL,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateReds);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "reds",
  },
  {
    category: ThemeCategory.SEQUENTIAL,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateViridis);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "viridis",
  },
  {
    category: ThemeCategory.SEQUENTIAL,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateInferno);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "inferno",
  },
  {
    category: ThemeCategory.SEQUENTIAL,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolatePuRd);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "purple/red",
  },
  {
    category: ThemeCategory.SEQUENTIAL,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateOrRd);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "orange/red",
  },
  {
    category: ThemeCategory.SEQUENTIAL,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateYlGnBu);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "yellow/green/blue",
  },
  {
    category: ThemeCategory.SEQUENTIAL,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateYlOrRd);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "yellow/orange/red",
  },
  {
    category: ThemeCategory.DIVERGING,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateBrBG);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "brown/blueGreen",
  },
  {
    category: ThemeCategory.DIVERGING,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolatePRGn);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "purple/green",
  },
  {
    category: ThemeCategory.DIVERGING,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolatePiYG);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "pink/yellowGreen",
  },
  {
    category: ThemeCategory.DIVERGING,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolatePuOr);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "purple/orange",
  },
  {
    category: ThemeCategory.DIVERGING,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateRdBu);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "red/blue",
  },
  {
    category: ThemeCategory.DIVERGING,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateRdGy);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "red/gray",
  },
  {
    category: ThemeCategory.DIVERGING,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateRdYlBu);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "red/yellow/blue",
  },
  {
    category: ThemeCategory.DIVERGING,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateRdYlGn);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "red/yellow/green",
  },
  {
    category: ThemeCategory.DIVERGING,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleSequential([0, numberOfBuckets - 1], interpolateSpectral);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "spectral",
  },
  {
    category: ThemeCategory.QUALITATIVE,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleOrdinal([0, numberOfBuckets - 1], schemeCategory10);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "schemeCategory10",
  },
  {
    category: ThemeCategory.QUALITATIVE,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleOrdinal([0, numberOfBuckets - 1], schemePaired);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "schemePaired",
  },
  {
    category: ThemeCategory.QUALITATIVE,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleOrdinal([0, numberOfBuckets - 1], schemePastel1);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "schemePastel1",
  },
  {
    category: ThemeCategory.QUALITATIVE,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleOrdinal([0, numberOfBuckets - 1], schemeSet1);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "schemeSet1",
  },
  {
    category: ThemeCategory.QUALITATIVE,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleOrdinal([0, numberOfBuckets - 1], schemeSet3);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "schemeSet3",
  },
  {
    category: ThemeCategory.QUALITATIVE,
    getColorFn: (numberOfBuckets: number) => {
      const colorFn = scaleOrdinal([0, numberOfBuckets - 1], schemeTableau10);
      return (index: number) => color(colorFn(index))!.formatHex() as HexColor;
    },
    name: "schemeTableau10",
  },
  {
    category: ThemeCategory.CUSTOM,
    getColorFn: () => () => "#000000" as HexColor,
    name: "custom",
  },
] as const satisfies ThemeDescriptor[];

export type ThemeName = (typeof themes)[number]["name"];
